import { useTracker } from '@archipro-website/tracker';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { detailCta } from '~/ui-rocco/pages/detail-shared';
import type { SloganColumnProps } from '@modules/page-builder/types';
import { trackArticleViewProfileClick } from '~/modules/tracking/util/trackArticleProfessionalCta';
import { RevampRichText } from './RevampRichText';
import { RevampProfessionalCtas } from './RevampProfessionalCtas';
import {
    REVAMP_QUOTE_FRAME,
    REVAMP_QUOTE_NAME_PROSE,
    REVAMP_QUOTE_PROSE,
    REVAMP_QUOTE_WIDTH_FULL,
    REVAMP_QUOTE_WIDTH_HALF,
} from '../../util/article-prose';

/**
 * - `half`: "Page Builder / Quote / half" — in one cell of the grid, the
 *   hairline 129px in, the quote on a 535px measure, the name and the View
 *   Profile / Contact pair under it. `standard` is the same component, and
 *   is kept as a name for the callers that pass it.
 * - `fullWidth`: "Page Builder / Quote / full" — across the page on a 1160px
 *   measure, with a single outlined View Profile pill.
 */
type SloganVariant = 'standard' | 'fullWidth' | 'half';

interface RevampSloganModuleProps {
    column: SloganColumnProps;
    professional?: { title: string; link: string };
    variant?: SloganVariant;
}

/**
 * Slogan module (Figma "Page Builder / Slogan"): left hairline, the slogan,
 * the professional's name as the attribution when the module's ShowFullName
 * toggle is on, and, when its ShowCtaButtons toggle is on, CTA buttons for that
 * professional — the View Profile / Contact pill pair, or a single View Profile
 * pill in the full-width variant.
 *
 * Both are derived from the article's professional rather than page-builder
 * content, and the two toggles are independent, so an author can credit the
 * professional without the pills or vice versa. The attribution links to their
 * detail page.
 */
export const RevampSloganModule = ({
    column,
    professional,
    variant = 'standard',
}: RevampSloganModuleProps) => {
    const tracker = useTracker();
    // The attribution is the professional's own name, not authored copy, so
    // there is nothing to type in the editor. Articles with no professional are
    // ArchiPro's own editorial.
    const attributionName = professional?.title || 'ArchiPro';

    return (
        <div className="w-full">
            {/* The rule sits on the left edge of the full-bleed landscape image
                above it — 200px on the 1920 frame, which is that module's own
                margin. Its own 48px of section padding is part of the sum. */}
            <div
                className={cn(
                    REVAMP_QUOTE_FRAME,
                    // The measure the design gives each: 1160 across the page,
                    // 535 in a cell.
                    variant === 'fullWidth'
                        ? REVAMP_QUOTE_WIDTH_FULL
                        : REVAMP_QUOTE_WIDTH_HALF,
                )}
            >
                {/* The quote and the name are one group, spaced tighter
                    than the container's own gap: the name credits the line
                    above it, so it belongs with that line rather than sitting
                    halfway between it and the buttons. */}
                <div className="flex w-full flex-col gap-8 md:gap-[3.875rem]">
                    <RevampRichText
                        contentHTML={column.contentHTML}
                        // One setting in every placement: the design's 80px
                        // quote, and only the measure differs between them.
                        className={REVAMP_QUOTE_PROSE}
                    />
                    {column.showFullName && (
                        <p className={REVAMP_QUOTE_NAME_PROSE}>
                            {/* Only linked when there is a profile to reach; an
                            ArchiPro editorial article has no professional. */}
                            {professional?.link ? (
                                <RoccoLink
                                    to={professional.link}
                                    onClick={() =>
                                        trackArticleViewProfileClick(
                                            tracker,
                                            'sloganAttribution',
                                        )
                                    }
                                    className="text-inherit transition-opacity hover:opacity-80"
                                >
                                    {attributionName}
                                </RoccoLink>
                            ) : (
                                attributionName
                            )}
                        </p>
                    )}
                </div>
                {column.showCtaButtons &&
                    professional &&
                    (variant === 'fullWidth' ? (
                        <RoccoLink
                            to={professional.link}
                            onClick={() =>
                                trackArticleViewProfileClick(tracker, 'slogan')
                            }
                            // The detail pages' hairline CTA, so the lone
                            // View Profile on the full-width slogan is the
                            // same control as the pair below every other
                            // module.
                            className={detailCta('outlined-light')}
                        >
                            View Profile
                        </RoccoLink>
                    ) : (
                        <RevampProfessionalCtas
                            professional={professional}
                            placement="slogan"
                        />
                    ))}
            </div>
        </div>
    );
};
